import React, { useState, useEffect, useRef } from 'react';
import './MainHeader.css';
import logo from '../../office-logo.svg';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import PersonIcon from '@mui/icons-material/Person';
import LoginPopover from '../auth/LoginPopover';
import { EmployeeDocumentService } from '../../services/employee-document.service';
import { EmployeeDocumentType } from '../../interfaces/employee-document.interface';

const MainHeader = () => {
    const { user, logout } = useAuth();
    const { t } = useTranslation('shared');
    const navigate = useNavigate();
    // Go to /login explicitly (as the sidebar does). Letting the route guard bounce the now
    // logged-out page to /login records it as a deep link, and the NEXT person to sign in on this
    // browser would be sent to the previous user's page instead of their own dashboard.
    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };
    const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const profileBlobUrlRef = useRef<string | null>(null);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        EmployeeDocumentService.getMyDocuments()
            .then(docs => {
                const pic = docs.find(d => d.documentType === EmployeeDocumentType.PASSPORT_SIZE_PICTURE);
                if (!pic || cancelled) return;
                return EmployeeDocumentService.getDocumentBlobUrl(pic.id);
            })
            .then(url => {
                if (!url || cancelled) return;
                profileBlobUrlRef.current = url;
                setProfileImageUrl(url);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            if (profileBlobUrlRef.current) window.URL.revokeObjectURL(profileBlobUrlRef.current);
        };
    }, [user]);

    return (
        <header className="main-header">
            <div className="logo-container">
                <img src={logo} alt={t('header.logoAlt')} className="logo-image" />
            </div>

            {user ? (
                <div className="profile-section">
                    <LanguageSwitcher />
                    <span className="username-display">{user.name}</span>
                    <Link to="/profile" className="profile-link">
                        <Avatar
                            src={profileImageUrl ?? undefined}
                            sx={{ width: 28, height: 28 }}
                        >
                            {!profileImageUrl && <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        {t('header.myProfile')}
                    </Link>
                    <button onClick={handleLogout} className="logout-button">
                        {t('header.logout')}
                    </button>
                </div>
            ) : (
                <div className="profile-section">
                    <LanguageSwitcher />
                    <Button
                        variant="contained"
                        onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                        sx={{
                            backgroundColor: '#006a4e',
                            '&:hover': { backgroundColor: '#00543e' },
                            textTransform: 'none',
                        }}
                    >
                        {t('header.login')}
                    </Button>
                    <LoginPopover
                        anchorEl={loginAnchorEl}
                        open={Boolean(loginAnchorEl)}
                        onClose={() => setLoginAnchorEl(null)}
                    />
                </div>
            )}
        </header>
    );
};

export default MainHeader;
